import { Option, Select, Typography } from '@material-tailwind/react'
import { useTranslation } from 'react-i18next'

import { DELEGABLE_ROLES, roleKey } from '../utils/constants/sharing.constants'

interface SharingRoleSelectorProps {
  /** Selected role, as the string the Select works with. */
  value?: string
  /** Role already assigned, when editing an existing share. */
  currentRole?: number
  disabled?: boolean
  darkMode?: boolean
  onChange: (value?: string) => void
}

/**
 * The role dropdown for the sharing panel.
 *
 * Options are derived from DELEGABLE_ROLES rather than written by hand, so adding
 * or removing an assignable role is one line in one place and the dropdown cannot
 * offer what the backend refuses.
 *
 * A share whose current role is no longer delegable — the ten existing `editor`
 * rows — still shows that role: preselected, disabled and qualified ("Editor — no
 * longer assignable"). The owner sees what is there, learns they cannot reassign
 * it, and can still change it to something delegable.
 */
const SharingRoleSelector: React.FC<SharingRoleSelectorProps> = ({
  value,
  currentRole,
  disabled,
  darkMode,
  onChange,
}) => {
  const { t } = useTranslation()

  const showCurrentAsFrozen = currentRole != null && !DELEGABLE_ROLES.includes(currentRole)
  const selectedRole = value ? Number(value) : undefined
  const hint = selectedRole != null ? t(roleKey(selectedRole, 'hint'), '') : ''

  return (
    <div className="flex flex-col gap-1">
      <Select
        label={t('sharing.role')}
        onChange={onChange}
        value={value}
        disabled={disabled}
        menuProps={{ className: 'dark:bg-primary' }}
        data-testid="sharing-role-select"
        {...(darkMode && { color: 'blue-gray' })}
        labelProps={{
          className:
            'text-gray-600 dark:!text-gray-200 peer-focus:!text-gray-800 dark:peer-focus:!text-gray-100',
        }}
      >
        {[...(showCurrentAsFrozen ? [currentRole] : []), ...DELEGABLE_ROLES].map((role) => {
          const frozen = role === currentRole && showCurrentAsFrozen
          const label = frozen
            ? `${t(roleKey(role, 'label'))} — ${t(roleKey(role, 'unavailable'))}`
            : t(roleKey(role, 'label'))

          return (
            <Option
              key={role}
              value={String(role)}
              disabled={frozen}
              data-testid={`sharing-role-option-${role}`}
              {...(value === String(role) && { className: 'bg-gray-200 dark:bg-primary-300' })}
            >
              <span className="text-primary dark:text-white">{label}</span>
            </Option>
          )
        })}
      </Select>

      {!!hint && (
        <Typography variant="small" className="font-normal" data-testid="sharing-role-hint">
          {hint}
        </Typography>
      )}
    </div>
  )
}

export default SharingRoleSelector
